import { fetchUtils, type DataProvider, type UpdateParams } from "ra-core";
import { createManagementClient, resolveAccessToken } from "./authProvider";
import { ManagementClient, type Management } from "auth0";
import { unflattenDomainMetadata } from "./components/custom-domains/domainMetadataUtils";
import {
  EMAIL_TEMPLATE_DEFINITIONS,
  getTemplateLabel,
} from "./resources/email-templates/template-names";
import { resolveOrganizationMemberDeletion } from "./utils/organizationMembers";

function isNotFoundError(err: unknown): boolean {
  return (
    !!err &&
    typeof err === "object" &&
    "statusCode" in err &&
    (err as { statusCode?: number }).statusCode === 404
  );
}

type CustomTextArgs = Parameters<
  ManagementClient["prompts"]["customText"]["get"]
>;
type MembersListQuery = NonNullable<
  Parameters<ManagementClient["organizations"]["members"]["list"]>[1]
>;
type PartialGroup = Parameters<
  ManagementClient["prompts"]["partials"]["get"]
>[0];
type CustomTextPrompt = CustomTextArgs[0];
type CustomTextLanguage = CustomTextArgs[1];

type EmailTemplateDefaults = Record<string, { body: string; subject: string }>;

async function fetchEmailTemplateDefaults(
  apiUrl: string,
  httpClient: typeof fetchUtils.fetchJson,
  tenantId?: string,
): Promise<EmailTemplateDefaults> {
  const headers = new Headers();
  if (tenantId) headers.set("tenant-id", tenantId);
  try {
    const res = await httpClient(`${apiUrl}/api/v2/email-templates/defaults`, {
      headers,
    });
    const entries: Array<{ name: string; body: string; subject: string }> =
      res.json ?? [];
    const out: EmailTemplateDefaults = {};
    for (const e of entries) {
      out[e.name] = { body: e.body, subject: e.subject };
    }
    return out;
  } catch {
    return {};
  }
}

async function fetchEmailTemplateRecord(
  client: ManagementClient,
  name: string,
  defaults: EmailTemplateDefaults = {},
): Promise<Record<string, unknown>> {
  const def = defaults[name];
  try {
    const tpl = await client.emailTemplates.get(
      name as Parameters<typeof client.emailTemplates.get>[0],
    );
    return {
      ...tpl,
      id: name,
      template: name,
      label: getTemplateLabel(name),
      is_override: true,
      default_html: def?.body ?? "",
      default_subject: def?.subject ?? "",
    };
  } catch (err) {
    if (isNotFoundError(err)) {
      return {
        id: name,
        template: name,
        label: getTemplateLabel(name),
        is_override: false,
        enabled: true,
        subject: def?.subject ?? "",
        body: def?.body ?? "",
        from: "",
        default_html: def?.body ?? "",
        default_subject: def?.subject ?? "",
      };
    }
    throw err;
  }
}

// Loosely typed JSON object as returned by the management API
type ApiRecord = Record<string, unknown>;

// Unwrap the SDK response wrapper ({ response }) if present
// List responses are either a bare array or an object holding the array under a key
type ListResponse<K extends string> =
  | ApiRecord[]
  | ({ total?: number } & { [P in K]?: ApiRecord[] });

function unwrapResponse<T = ApiRecord>(result: unknown): T {
  return ((result as { response?: unknown }).response || result) as T;
}

function stringify(obj: ApiRecord): string {
  return Object.entries(obj)
    .filter(([_, value]) => value !== undefined)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("&");
}

// Remove null and undefined values from an object (used for create operations where empty form fields become null)
function removeNullValues(data: ApiRecord): ApiRecord {
  const cleaned: ApiRecord = {};
  for (const key of Object.keys(data)) {
    if (data[key] !== null && data[key] !== undefined) {
      cleaned[key] = data[key];
    }
  }
  return cleaned;
}

function removeExtraFields(params: UpdateParams) {
  // delete params.data?.id; // this is required for patch... but not for put?
  delete params.data?.tenant_id;
  delete params.data?.updated_at;
  delete params.data?.created_at;
  delete params.data?.identities;

  // hmmmmm, this is an issue we have here with mismatching structure?
  // seems like we need to modify our endpoints to accept connections.
  // TBD with Markus
  delete params.data?.connections;

  // actually Auth0 does not require this for patching. seems dangerous not to rely on an auto-id
  // as may get rejected for having the same id
  delete params.data?.id;
  // for user we don't want to include this
  delete params.data?.user_id;

  // extra user fields
  delete params.data?.last_login;
  delete params.data?.provider;

  // Remove undefined properties (null is preserved to signal "unset this field" in PATCH)
  Object.keys(params.data).forEach((key) => {
    if (params.data[key] === undefined) {
      delete params.data[key];
    }
  });

  return params;
}

function parseResource(resourcePath: string) {
  return resourcePath.split("/").pop() || resourcePath;
}

// Escape user-supplied filter values before interpolating them into a Lucene
// query string. Escapes backslashes, double quotes, and Lucene reserved
// operators to prevent injection via quote-breaking or special syntax.
function escapeLuceneValue(value: unknown): string {
  const str = String(value);
  return str.replace(
    /[\\"+\-!(){}[\]^~*?:/]|&&|\|\|/g,
    (match) => `\\${match}`,
  );
}

// A refresh token as returned by GET /users/{id}/refresh-tokens. Only `id` is
// relied on here; the rest passes through to the record for the tab to render.
interface RefreshTokenResponse {
  id: string;
  [key: string]: unknown;
}

// Maps react-admin resource names to Auth0 API paths when they differ
const API_PATH_MAP: Record<string, string> = {
  actions: "actions/actions",
  "action-executions": "actions/executions",
};

function getApiPath(resource: string): string {
  return API_PATH_MAP[resource] || resource;
}

// Virtual sub-resource: scopes nested under resource-servers.
// Stored as an array on the parent resource server, but exposed to react-admin
// as its own resource so standard List/Create/Edit components work.
const SCOPE_RES = "resource-server-scopes";

const TOKEN_EXCHANGE_PROFILES_RES = "token-exchange-profiles";
const TOKEN_EXCHANGE_PROFILES_PAGE_SIZE = 100;
const TOKEN_EXCHANGE_PROFILES_MAX_PAGES = 50;

// Only these fields may be sent on PATCH; `action_id` and `type` are fixed
// at creation and the API rejects the request if they are present.
function pickTokenExchangeProfileUpdate(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of ["name", "subject_token_type", "jwt_verification"]) {
    if (data[key] !== undefined && data[key] !== null) out[key] = data[key];
  }
  return out;
}

function pickTokenExchangeProfileCreate(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {
    name: data.name,
    subject_token_type: data.subject_token_type,
    type: data.type ?? "custom_authentication",
  };
  // The API requires exactly one of the two. The form keeps both around while
  // the user switches modes, so send only the one that wins.
  if (typeof data.action_id === "string" && data.action_id) {
    out.action_id = data.action_id;
  } else if (data.jwt_verification) {
    out.jwt_verification = data.jwt_verification;
  }
  return out;
}

function splitScopeId(id: string | number): readonly [string, string] {
  const s = String(id);
  const i = s.indexOf(":");
  if (i === -1) return ["", ""] as const;
  return [s.slice(0, i), s.slice(i + 1)] as const;
}

async function fetchResourceServerScopes(
  managementClient: ManagementClient,
  rsId: string,
): Promise<{ scopes: Management.ResourceServerScope[] }> {
  const result = await managementClient.resourceServers.get(rsId);
  const rs = unwrapResponse(result);
  return { scopes: Array.isArray(rs.scopes) ? rs.scopes : [] };
}

// Helper to normalize SDK response format variations
function normalizeSDKResponse(
  result: unknown,
  resourceKey: string,
): { data: ApiRecord[]; total: number } {
  const response = ((result as { response?: unknown }).response ||
    {}) as ApiRecord;

  // Handle direct array format
  if (Array.isArray(response)) {
    return { data: response, total: response.length };
  }

  // Handle SDK wrapper format with resource key
  if (response[resourceKey]) {
    const items = response[resourceKey] as ApiRecord[];
    return {
      data: items,
      // Auth0 sends the overall count as `total`; `length` (the page size
      // there) only matters for older servers that reported the count in it.
      total:
        (response.total as number | undefined) ||
        (response.length as number | undefined) ||
        items.length,
    };
  }

  // Handle result itself being the array
  if (Array.isArray(result)) {
    return { data: result, total: result.length };
  }

  // Fallback to empty array
  return { data: [], total: 0 };
}

// Helper to create headers with tenant ID
function createHeaders(tenantId?: string): Headers {
  const headers = new Headers();
  if (tenantId) {
    headers.set("tenant-id", tenantId);
  }
  return headers;
}

// Helper for client-side paging, sorting, and search on pre-fetched data
interface ClientSideListParams {
  data: ApiRecord[];
  page: number;
  perPage: number;
  sortField?: string;
  sortOrder?: "ASC" | "DESC";
  searchQuery?: string;
  searchFields?: string[];
  idKey?: string;
}

function clientSideListHandler({
  data,
  page,
  perPage,
  sortField,
  sortOrder,
  searchQuery,
  searchFields = ["name"],
  idKey = "id",
}: ClientSideListParams): { data: ApiRecord[]; total: number } {
  let filtered = data;

  // Apply client-side search filter
  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filtered = data.filter((item: ApiRecord) =>
      searchFields.some((field) =>
        item[field]?.toString().toLowerCase().includes(query),
      ),
    );
  }

  // Apply client-side sorting
  if (sortField) {
    filtered = [...filtered].sort((a: ApiRecord, b: ApiRecord) => {
      const aVal = a[sortField] || "";
      const bVal = b[sortField] || "";
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortOrder === "DESC" ? -comparison : comparison;
    });
  }

  // Apply client-side pagination
  const total = filtered.length;
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paged = filtered.slice(startIndex, endIndex);

  return {
    data: paged.map((item: ApiRecord) => ({
      id: item[idKey] || item.id,
      ...item,
    })),
    total,
  };
}

// Helper to handle singleton resource fetching
async function fetchSingleton(
  resource: string,
  fetcher: () => Promise<unknown>,
): Promise<{ data: ApiRecord[]; total: number }> {
  try {
    const result = await fetcher();
    const data = unwrapResponse(result);
    // Spread data first, then override id to ensure it matches the resource name
    return {
      data: [{ ...data, id: resource }],
      total: 1,
    };
  } catch (error) {
    console.error(`Error in getList for ${resource}:`, error);
    return {
      data: [{ id: resource }],
      total: 1,
    };
  }
}

// Extended data provider surface for non-CRUD actions (e.g. signing-key
// rotation/revocation) that don't map cleanly to react-admin's standard verbs.
// Consumers call these via useDataProvider<AuthHeroDataProvider>().
export type SigningKeyType = "jwt_signing" | "saml_encryption";

export interface RotateSigningKeyOptions {
  type?: SigningKeyType;
  validityDays?: number;
  /**
   * Publish the new key now, but only start signing with it after this many
   * days — the window in which a SAML service provider can be given the new
   * certificate without any login failing.
   */
  activateInDays?: number;
  /** How long the outgoing key stays valid after the new one activates. */
  graceDays?: number;
}

export interface AuthHeroDataProvider extends DataProvider {
  rotateSigningKeys: (options?: RotateSigningKeyOptions) => Promise<void>;
  revokeSigningKey: (kid: string, type?: SigningKeyType) => Promise<void>;
  renewSigningKey: (
    kid: string,
    options?: { type?: SigningKeyType; validityDays?: number },
  ) => Promise<void>;
  revokeUserRefreshTokens: (userId: string) => Promise<void>;
  uploadCustomDomainCertificate: (
    id: string,
    cert: { certificate: string; private_key: string },
  ) => Promise<void>;
  sendTestEmailTemplate: (
    templateName: string,
    payload: {
      to: string;
      body?: string;
      subject?: string;
      from?: string;
      language?: string;
    },
  ) => Promise<void>;
  tryHook: (
    hookId: string,
    payload: { user_id: string },
  ) => Promise<{ ok: boolean; status?: number; body?: string; error?: string }>;
  /**
   * Download the tenant's durable data as a gzipped JSON-lines file. Returns a
   * `Blob` the caller can hand straight to the browser download API, so the
   * compressed payload is never buffered as a JS string.
   */
  exportTenantData: (includePasswordHashes?: boolean) => Promise<Blob>;
  /**
   * Replay a previously exported file into the current tenant. Accepts the raw
   * `Blob`/`File` (plain or gzipped) and streams it straight to the server, so
   * the UI never has to decode it into memory first.
   */
  importTenantData: (
    file: Blob | File,
    includePasswordHashes?: boolean,
  ) => Promise<{
    counts: Record<string, number>;
    errors: { entity: string; error: string }[];
  }>;
  /**
   * Durable tenant lifecycle operations (issue #1026). Control-plane
   * routes — available only when the server carries the operations
   * adapters (404 otherwise, 501 when no executor is configured).
   */
  listTenantOperations: (
    tenantId: string,
    params?: { page?: number; perPage?: number },
  ) => Promise<{
    operations: TenantOperationRecord[];
    start: number;
    limit: number;
    length: number;
  }>;
  getTenantOperation: (
    operationId: string,
  ) => Promise<
    TenantOperationRecord & { events: TenantOperationEventRecord[] }
  >;
  createTenantOperation: (
    tenantId: string,
    kind: "upgrade" | "seed" | "backup" | "sync-defaults" | "detach",
    input?: {
      dry_run?: boolean;
      force?: boolean;
      prune?: boolean;
      clean?: boolean;
    },
  ) => Promise<TenantOperationRecord>;

  /**
   * Sync status of a tenant and where its inherited rows come from. A tenant
   * can read its own; the control plane any tenant.
   */
  getConfigSyncStatus: (tenantId: string) => Promise<ConfigSyncStatus>;
  /**
   * Compares a tenant's config with what its parents publish, read-only and on
   * demand. `ok` is the pass criterion.
   */
  verifyConfigSync: (tenantId: string) => Promise<ConfigSyncVerification>;

  /**
   * Fleet rollouts of synced config (issue #1499). Control-plane routes,
   * mounted only when the server carries the rollouts adapter.
   */
  listRollouts: (params?: {
    page?: number;
    perPage?: number;
    status?: RolloutRecord["status"];
  }) => Promise<{ rollouts: RolloutRecord[]; length: number }>;
  getRollout: (
    rolloutId: string,
  ) => Promise<RolloutRecord & { summary: RolloutSummary }>;
  createRollout: (input: CreateRolloutInput) => Promise<RolloutRecord>;
  pauseRollout: (rolloutId: string, reason?: string) => Promise<RolloutRecord>;
  resumeRollout: (rolloutId: string) => Promise<RolloutRecord>;

  /**
   * Tenant-team self-management (#1137). These hit `/api/v2/tenant-members`,
   * which manages who administers THIS tenant — control-plane organization
   * membership + org-scoped roles + invitations — not the tenant's end users.
   * The server pins the org to the caller's token, so no organization id is
   * passed. Available only when the server enables the resource (404 / 403
   * otherwise).
   */
  listTeamMembers: (params?: {
    page?: number;
    perPage?: number;
    q?: string;
  }) => Promise<{
    members: TeamMemberRecord[];
    start: number;
    limit: number;
    total: number;
  }>;
  addTeamMembers: (userIds: string[]) => Promise<TeamChangeResult>;
  removeTeamMembers: (userIds: string[]) => Promise<TeamChangeResult>;
  assignTeamMemberRoles: (
    userId: string,
    roleIds: string[],
  ) => Promise<TeamChangeResult>;
  removeTeamMemberRoles: (
    userId: string,
    roleIds: string[],
  ) => Promise<TeamChangeResult>;
  listAssignableTeamRoles: (q?: string) => Promise<TeamRoleRecord[]>;
  listTeamInvitations: () => Promise<TeamInvitationRecord[]>;
  createTeamInvitation: (input: {
    email: string;
    inviterName?: string;
    roles?: string[];
    sendInvitationEmail?: boolean;
  }) => Promise<TeamChangeResult & { invitation?: TeamInvitationRecord }>;
  revokeTeamInvitation: (invitationId: string) => Promise<TeamChangeResult>;
  /**
   * The team changes this tenant asked for, newest first (issue #1550). Only
   * a server that registers changes as requests has them: 501 / 404 / 403
   * otherwise.
   */
  listTeamRequests: (params?: {
    status?: TeamRequestStatus;
    perPage?: number;
  }) => Promise<TeamRequestRecord[]>;
}

/**
 * What a team write came back with. A server that registers changes as
 * requests answers `202`: the change was accepted and the control plane
 * carries it out, which the request's `status` then tells. A server that acts
 * at once answers 2xx with `requested: false`.
 */
export type TeamChangeResult =
  | { requested: false }
  | { requested: true; id: string; status: TeamRequestStatus };

export type TeamRequestStatus = "pending" | "succeeded" | "failed";

export type TeamRequestKind =
  | "invite"
  | "add_members"
  | "remove_members"
  | "assign_roles"
  | "remove_roles"
  | "revoke_invitation";

export interface TeamRequestRecord {
  id: string;
  kind: TeamRequestKind;
  status: TeamRequestStatus;
  requested_by: string;
  payload: Record<string, unknown>;
  /** What the request produced, for example `{ invitation_id }`. */
  result?: Record<string, unknown> | null;
  /** Why a `failed` request failed. */
  error?: string | null;
  created_at: string;
  updated_at: string;
  finished_at?: string | null;
}

export interface TeamRoleRecord {
  id: string;
  name?: string;
  description?: string;
}

export interface TeamMemberRecord {
  user_id: string;
  email?: string;
  name?: string;
  picture?: string;
  roles: TeamRoleRecord[];
}

export interface TeamInvitationRecord {
  id: string;
  organization_id: string;
  invitee?: { email?: string };
  inviter?: { name?: string };
  created_at?: string;
  expires_at?: string;
}

/** A team write: `202` means the change was registered, not made. */
function teamChangeResult(res: {
  status: number;
  json: unknown;
}): TeamChangeResult {
  if (res.status !== 202) return { requested: false };
  const body = (res.json ?? {}) as { id?: string; status?: TeamRequestStatus };
  return {
    requested: true,
    id: body.id ?? "",
    status: body.status ?? "pending",
  };
}

// Wire shapes of config inheritance (issues #1495, #1499, #1500).
export interface ConfigSyncDifference {
  entity: string;
  key: string;
  kind: "missing" | "extra" | "changed" | "stale";
  fields?: string[];
}

export interface ConfigSyncVerification {
  tenant_id: string;
  config_sync: "inherit-on-read" | "synced" | "detached";
  method: "read-time" | "sync-dry-run" | "not-applicable";
  ok: boolean;
  differences: ConfigSyncDifference[];
  errors: string[];
}

export interface ConfigSyncStatus {
  tenant_id: string;
  parent_tenant_id: string | null;
  config_sync: "inherit-on-read" | "synced" | "detached";
  config_sync_hash: string | null;
  config_synced_at: string | null;
  expected_hash: string | null;
  drifted: boolean;
  /** Ancestors the config resolves from, nearest first. */
  chain: string[];
  /** Inherited row id (or singleton name) -> the tenant that published it. */
  inherited: Record<string, string>;
}

export interface RolloutRecord {
  id: string;
  kind: string;
  status: "pending" | "canary" | "rolling" | "paused" | "done" | "failed";
  config?: Record<string, unknown> | null;
  paused_reason?: string | null;
  initiated_by?: string | null;
  created_at: string;
  updated_at: string;
  finished_at?: string | null;
}

export interface RolloutSummary {
  total: number;
  by_status: Record<string, number>;
  by_wave: Array<{
    wave: number;
    total: number;
    by_status?: Record<string, number>;
  }>;
}

export interface CreateRolloutInput {
  root_tenant_id?: string;
  tenant_ids?: string[];
  canary?: number;
  waves?: number[];
  failure_threshold?: number;
  max_concurrent?: number;
  dry_run?: boolean;
  force?: boolean;
}

// Wire shapes of the tenant-operations management API (issue #1026).
export interface TenantOperationRecord {
  id: string;
  tenant_id: string | null;
  kind:
    | "provision"
    | "seed"
    | "upgrade"
    | "backup"
    | "deprovision"
    | "users_import"
    | "sync-defaults"
    | "detach";
  status: "pending" | "running" | "succeeded" | "failed" | "cancelled";
  current_step?: string | null;
  engine: string;
  error?: string | null;
  initiated_by?: string | null;
  /** Kind-specific parameters, e.g. `{ dry_run: true }` for a sync. */
  input?: Record<string, unknown> | null;
  /** The outcome, e.g. the changes a `sync-defaults` run made or would make. */
  result?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  finished_at?: string | null;
}

export interface TenantOperationEventRecord {
  id: string;
  operation_id: string;
  step: string;
  outcome:
    | "started"
    | "succeeded"
    | "failed"
    | "retried"
    | "skipped"
    | "reconciled";
  detail?: Record<string, unknown> | null;
  attempt: number;
  created_at: string;
}

/**
 * Maps react-admin queries to the auth0 management api
 * Uses HTTP client for all API calls with custom headers for tenant isolation
 */
export default (
  apiUrl: string,
  httpClient = fetchUtils.fetchJson,
  tenantId?: string,
  domain?: string,
): AuthHeroDataProvider => {
  // Get or create management client for SDK calls
  let managementClientPromise: Promise<ManagementClient> | null = null;
  const getManagementClient = async () => {
    if (!managementClientPromise) {
      if (!apiUrl) {
        throw new Error(
          "API URL is not configured. Please set restApiUrl in domain configuration or VITE_SIMPLE_REST_URL environment variable.",
        );
      }
      managementClientPromise = createManagementClient(
        apiUrl,
        tenantId,
        domain,
      );
    }
    return managementClientPromise;
  };

  return {
    getList: async (resourcePath, params) => {
      const resource = parseResource(resourcePath);
      const { page = 1, perPage } = params.pagination || {};
      const { field, order } = params.sort || {};
      const managementClient = await getManagementClient();

      // SDK resource handlers configuration
      const sdkHandlers: Record<
        string,
        {
          fetch: (client: ManagementClient) => Promise<unknown>;
          resourceKey: string;
          idKey: string;
        }
      > = {
        users: {
          fetch: (client) => {
            const { q: rawQ, ...filterPairs } = params.filter || {};
            const extraLucene = Object.entries(filterPairs)
              .filter(([, v]) => v !== undefined && v !== null && v !== "")
              .map(([k, v]) => `${k}:"${escapeLuceneValue(v)}"`)
              .join(" ");
            const mergedQ =
              [rawQ, extraLucene].filter(Boolean).join(" ") || undefined;
            return client.users.list({
              page: page - 1,
              per_page: perPage,
              sort:
                field && order
                  ? `${field}:${order === "DESC" ? "-1" : "1"}`
                  : undefined,
              q: mergedQ,
              include_totals: true,
            });
          },
          resourceKey: "users",
          idKey: "user_id",
        },
        // clients handled separately with client-side paging/search
        // connections handled separately with client-side paging/search
        roles: {
          fetch: (client) =>
            client.roles.list({
              page: page - 1,
              per_page: perPage,
            }),
          resourceKey: "roles",
          idKey: "id",
        },
        "resource-servers": {
          fetch: (client) => client.resourceServers.list(),
          resourceKey: "resource_servers",
          idKey: "id",
        },
        // Organizations handled separately with client-side paging/search
        // Logs removed from SDK handlers - using HTTP directly for full control
        rules: {
          fetch: (client) => client.rules.list(),
          resourceKey: "rules",
          idKey: "id",
        },
        "client-grants": {
          fetch: (client) =>
            client.clientGrants.list({
              page: page - 1,
              per_page: perPage,
              ...(params.filter?.client_id && {
                client_id: params.filter.client_id,
              }),
            }),
          resourceKey: "client_grants",
          idKey: "id",
        },
      };

      // Virtual scopes-as-list under resource servers
      if (resource === SCOPE_RES) {
        const rsId = params.filter?.resource_server_id;
        if (!rsId) return { data: [], total: 0 };
        const { scopes } = await fetchResourceServerScopes(
          managementClient,
          rsId,
        );
        return clientSideListHandler({
          data: scopes.map((s: Management.ResourceServerScope) => ({
            id: `${rsId}:${s.value}`,
            resource_server_id: rsId,
            value: s.value,
            description: s.description,
          })),
          page,
          perPage: perPage || 25,
          sortField: field,
          sortOrder: order,
          searchQuery: params.filter?.q,
          searchFields: ["value", "description"],
        });
      }

      // The unified Triggers view needs every binding, not the SDK's default
      // first page. Forward paging, sort and search for bindings and forms.
      if (
        (resource === "hooks" || resource === "forms") &&
        !resourcePath.includes("/")
      ) {
        const query = {
          include_totals: true,
          page: page - 1,
          per_page: perPage,
          sort:
            field && order
              ? `${field}:${order === "DESC" ? "-1" : "1"}`
              : undefined,
          ...params.filter,
        };
        const response = await httpClient(
          `${apiUrl}/api/v2/${resource}?${stringify(query)}`,
          {
            headers: createHeaders(tenantId),
          },
        );
        const rows = Array.isArray(response.json)
          ? response.json
          : (response.json[resource] ?? []);
        return {
          data: rows.map((item: ApiRecord) => ({
            ...item,
            id: resource === "hooks" ? (item.hook_id ?? item.id) : item.id,
          })),
          total: response.json.total ?? rows.length,
        };
      }

      // Handle SDK resources (only for top-level resources, not nested paths like users/{id}/roles)
      const handler = sdkHandlers[resource];
      if (handler && !resourcePath.includes("/")) {
        const result = await handler.fetch(managementClient);
        const { data, total } = normalizeSDKResponse(
          result,
          handler.resourceKey,
        );
        return {
          data: data.map((item: ApiRecord) => ({
            id: item[handler.idKey] || item.id,
            ...item,
          })),
          total,
        };
      }

      // Handle singleton resources
      if (resource === "branding") {
        const branding = await managementClient.branding.get();
        let themes: Awaited<
          ReturnType<typeof managementClient.branding.themes.getDefault>
        > | null = null;
        try {
          themes = await managementClient.branding.themes.getDefault();
        } catch (err) {
          // 404 means no default theme configured yet; anything else should bubble up.
          if (
            !(
              err &&
              typeof err === "object" &&
              "statusCode" in err &&
              err.statusCode === 404
            )
          ) {
            throw err;
          }
        }
        return {
          data: [
            {
              ...branding,
              themes,
              id: resource,
            },
          ],
          total: 1,
        };
      }

      if (resource === "settings") {
        return fetchSingleton(resource, () =>
          managementClient.tenants.settings.get(),
        );
      }

      if (resource === "attack-protection") {
        const [bpd, bf, sip] = await Promise.all([
          managementClient.attackProtection.breachedPasswordDetection.get(),
          managementClient.attackProtection.bruteForceProtection.get(),
          managementClient.attackProtection.suspiciousIpThrottling.get(),
        ]);
        return {
          data: [
            {
              id: resource,
              breached_password_detection: bpd,
              brute_force_protection: bf,
              suspicious_ip_throttling: sip,
            },
          ],
          total: 1,
        };
      }

      // MFA singleton: a focused view of tenant settings (mfa + guardian_mfa_page)
      if (resource === "mfa") {
        return fetchSingleton(resource, () =>
          managementClient.tenants.settings.get(),
        );
      }

      // Handle prompts singleton resource
      if (resource === "prompts") {
        const headers = createHeaders(tenantId);
        try {
          const res = await httpClient(`${apiUrl}/api/v2/prompts`, { headers });
          // Also fetch custom text entries list
          let customTextEntries: Array<{ prompt: string; language: string }> =
            [];
          try {
            const customTextRes = await httpClient(
              `${apiUrl}/api/v2/prompts/custom-text`,
              { headers },
            );
            customTextEntries = customTextRes.json || [];
          } catch {
            // Custom text list might not exist yet
          }
          return {
            data: [{ ...res.json, customTextEntries, id: resource }],
            total: 1,
          };
        } catch (error) {
          console.error("Error fetching prompts:", error);
          return {
            data: [{ id: resource, customTextEntries: [] }],
            total: 1,
          };
        }
      }

      // Handle email-providers singleton resource
      if (resource === "email-providers") {
        try {
          const provider = await managementClient.emails.provider.get();
          return {
            data: [{ ...provider, id: resource }],
            total: 1,
          };
        } catch (err) {
          // Match getOne/update/delete: 404 means no provider configured.
          if (
            err &&
            typeof err === "object" &&
            "statusCode" in err &&
            err.statusCode === 404
          ) {
            return { data: [], total: 0 };
          }
          throw err;
        }
      }

      // Handle email-templates resource. No list endpoint upstream — fan out
      // to GET /{templateName} for each known template, treating 404 as
      // "still on bundled default".
      if (resource === "email-templates") {
        const defaults = await fetchEmailTemplateDefaults(
          apiUrl,
          httpClient,
          tenantId,
        );
        const records = await Promise.all(
          EMAIL_TEMPLATE_DEFINITIONS.map((def) =>
            fetchEmailTemplateRecord(managementClient, def.name, defaults),
          ),
        );
        const sortField = field ?? "label";
        const sortOrder = order ?? "ASC";
        const sorted = [...records].sort((a, b) => {
          const av = String((a as Record<string, unknown>)[sortField] ?? "");
          const bv = String((b as Record<string, unknown>)[sortField] ?? "");
          const cmp = av.localeCompare(bv);
          return sortOrder === "DESC" ? -cmp : cmp;
        });
        return { data: sorted, total: sorted.length };
      }

      // Handle custom-text resource (for individual custom text entries)
      if (resource === "custom-text") {
        const headers = createHeaders(tenantId);
        try {
          const res = await httpClient(`${apiUrl}/api/v2/prompts/custom-text`, {
            headers,
          });
          const entries = res.json || [];
          return {
            data: entries.map((e: { prompt: string; language: string }) => ({
              id: `${e.prompt}:${e.language}`,
              prompt: e.prompt,
              language: e.language,
            })),
            total: entries.length,
          };
        } catch (error) {
          console.error("Error fetching custom-text list:", error);
          return { data: [], total: 0 };
        }
      }

      // Handle custom-text-defaults: bundled default text shipped with
      // authhero. Backs placeholder rendering and field discovery in the
      // admin UI. Auth0 has no equivalent.
      if (resource === "custom-text-defaults") {
        const headers = createHeaders(tenantId);
        const defaultsQuery: Record<string, string> = {};
        if (params.filter?.language)
          defaultsQuery.language = params.filter.language;
        if (params.filter?.prompt) defaultsQuery.prompt = params.filter.prompt;
        const qs = stringify(defaultsQuery);
        const url = qs
          ? `${apiUrl}/api/v2/prompts/custom-text/defaults?${qs}`
          : `${apiUrl}/api/v2/prompts/custom-text/defaults`;
        try {
          const res = await httpClient(url, { headers });
          const entries = res.json || [];
          return {
            data: entries.map(
              (e: {
                prompt: string;
                language: string;
                custom_text: Record<string, Record<string, string>>;
              }) => ({
                id: `${e.prompt}:${e.language}`,
                prompt: e.prompt,
                language: e.language,
                custom_text: e.custom_text,
              }),
            ),
            total: entries.length,
          };
        } catch (error) {
          console.error("Error fetching custom-text defaults:", error);
          return { data: [], total: 0 };
        }
      }

      // Handle connections with client-side paging and search (fetch all, filter locally)
      if (resource === "connections" && !resourcePath.includes("/")) {
        const result = await managementClient.connections.list();
        const { data: allConnections } = normalizeSDKResponse(
          result,
          "connections",
        );

        return clientSideListHandler({
          data: allConnections,
          page,
          perPage: perPage || 10,
          sortField: field,
          sortOrder: order,
          searchQuery: params.filter?.q,
          searchFields: ["name", "strategy", "id"],
          idKey: "id",
        });
      }

      // Handle clients with client-side paging and search (fetch all, filter locally)
      if (resource === "clients" && !resourcePath.includes("/")) {
        const result = await managementClient.clients.list({
          page: 0,
          per_page: 500,
          include_totals: false,
        });
        const { data: allClients } = normalizeSDKResponse(result, "clients");

        return clientSideListHandler({
          data: allClients,
          page,
          perPage: perPage || 10,
          sortField: field,
          sortOrder: order,
          searchQuery: params.filter?.q,
          searchFields: ["name", "client_id"],
          idKey: "client_id",
        });
      }

      // Handle organizations with client-side paging and search (fetch 500, filter locally)
      if (resource === "organizations" && !resourcePath.includes("/")) {
        const result = await managementClient.organizations.list({
          from: "0",
          take: 500,
        });
        const { data: allOrgs } = normalizeSDKResponse(result, "organizations");

        return clientSideListHandler({
          data: allOrgs,
          page,
          perPage: perPage || 10,
          sortField: field,
          sortOrder: order,
          searchQuery: params.filter?.q,
          searchFields: ["name", "display_name"],
          idKey: "id",
        });
      }

      // Signing keys are served at /api/v2/keys/signing and use `kid` as their
      // identifier (no `id` field). The endpoint returns a plain array with no
      // pagination, so we fetch everything and let clientSideListHandler paginate.
      if (resource === "signing-keys") {
        const headers = createHeaders(tenantId);
        // `type` selects the key bucket (JWT vs SAML) rather than filtering
        // rows client-side: the two buckets are separate server-side.
        const keyType =
          typeof params.filter?.type === "string"
            ? params.filter.type
            : "jwt_signing";
        const res = await httpClient(
          `${apiUrl}/api/v2/keys/signing?type=${encodeURIComponent(keyType)}`,
          { headers },
        );
        const items: Array<Record<string, unknown>> = Array.isArray(res.json)
          ? (res.json as Array<Record<string, unknown>>)
          : [];
        const withIds = items.map((item) => ({ id: item.kid, ...item }));
        return clientSideListHandler({
          data: withIds,
          page,
          perPage: perPage || 25,
          sortField: field,
          sortOrder: order,
          searchQuery: params.filter?.q,
          searchFields: ["kid", "thumbprint", "fingerprint"],
          idKey: "id",
        });
      }

      // Handle custom-domains with client-side paging and search
      if (resource === "custom-domains" && !resourcePath.includes("/")) {
        const result = await managementClient.customDomains.list();
        const { data: allDomains } = normalizeSDKResponse(
          result,
          "custom_domains",
        );

        return clientSideListHandler({
          data: allDomains,
          page,
          perPage: perPage || 10,
          sortField: field,
          sortOrder: order,
          searchQuery: params.filter?.q,
          searchFields: ["domain", "custom_domain_id"],
          idKey: "custom_domain_id",
        });
      }

      // Token exchange profiles only support Auth0 checkpoint pagination
      // (`from`/`take`, `next` cursor) and return no total. A tenant has few
      // profiles, so follow the cursor to the end and page client-side.
      if (resource === TOKEN_EXCHANGE_PROFILES_RES) {
        const headers = createHeaders(tenantId);
        const all: Array<Record<string, unknown>> = [];
        let from: string | undefined;
        for (let i = 0; i < TOKEN_EXCHANGE_PROFILES_MAX_PAGES; i++) {
          const url = `${apiUrl}/api/v2/${TOKEN_EXCHANGE_PROFILES_RES}?${stringify(
            { from, take: TOKEN_EXCHANGE_PROFILES_PAGE_SIZE },
          )}`;
          const res = await httpClient(url, { headers });
          const body = res.json ?? {};
          const items = Array.isArray(body.token_exchange_profiles)
            ? body.token_exchange_profiles
            : [];
          all.push(...items);
          if (typeof body.next !== "string" || !body.next || !items.length) {
            break;
          }
          from = body.next;
        }

        return clientSideListHandler({
          data: all,
          page,
          perPage: perPage || 10,
          sortField: field,
          sortOrder: order,
          searchQuery: params.filter?.q,
          searchFields: ["name", "subject_token_type", "id"],
          idKey: "id",
        });
      }

      // Handle logs with direct HTTP for full control over query params
      if (resource === "logs") {
        const headers = createHeaders(tenantId);
        const { q: rawQ, from, to, ...filterPairs } = params.filter || {};
        const extraLucene = Object.entries(filterPairs)
          .filter(([, v]) => v !== undefined && v !== null && v !== "")
          .map(([k, v]) => `${k}:"${escapeLuceneValue(v)}"`)
          .join(" ");
        const mergedQ =
          [rawQ, extraLucene].filter(Boolean).join(" ") || undefined;
        const query: ApiRecord = {
          include_totals: true,
          page: page - 1,
          per_page: perPage,
          sort:
            field && order
              ? `${field}:${order === "DESC" ? "-1" : "1"}`
              : undefined,
          q: mergedQ,
          from,
          to,
        };
        const url = `${apiUrl}/api/v2/logs?${stringify(query)}`;

        const res = await httpClient(url, { headers });
        const response = res.json;
        const logsData = response.logs || response || [];
        const logsArray = Array.isArray(logsData) ? logsData : [];

        return {
          data: logsArray.map((log: ApiRecord) => ({
            id: log.log_id || log.id,
            ...log,
          })),
          total: response.total || logsArray.length,
        };
      }

      // Handle stats/daily endpoint
      if (resourcePath === "stats/daily") {
        try {
          const stats = await managementClient.stats.getDaily({
            from: params.filter?.from,
            to: params.filter?.to,
          });
          const data = Array.isArray(stats) ? stats : [];
          return {
            data: data.map((item, index) => ({
              id: item.date || index,
              ...item,
            })),
            total: data.length,
          };
        } catch (error) {
          console.error("Error fetching daily stats:", error);
          return { data: [], total: 0 };
        }
      }

      // User organizations - when filtering by user_id
      if (resource === "user-organizations" && params.filter?.user_id) {
        const userId = params.filter.user_id;
        const result = await managementClient.users.organizations.list(userId, {
          page: page - 1,
          per_page: perPage,
        });
        const response = unwrapResponse<ListResponse<"organizations">>(result);

        let organizationsData: ApiRecord[];
        let total: number;

        if (Array.isArray(response)) {
          organizationsData = response;
          total = response.length;
        } else if (response.organizations) {
          organizationsData = response.organizations;
          total = response.total || organizationsData.length;
        } else {
          organizationsData = [];
          total = 0;
        }

        return {
          data: organizationsData.map((org: ApiRecord) => ({
            id: org.id || org.organization_id,
            user_id: userId,
            name: org.name,
            display_name: org.display_name,
            branding: org.branding,
            metadata: org.metadata || {},
            token_quota: org.token_quota,
            created_at: org.created_at,
            updated_at: org.updated_at,
            ...org,
          })),
          total,
        };
      }

      // Use HTTP client for all other list operations
      const headers = createHeaders(tenantId);

      const { q, ...filterFields } = params.filter || {};
      const query: ApiRecord = {
        include_totals: true,
        page: page - 1,
        per_page: perPage,
        sort:
          field && order
            ? `${field}:${order === "DESC" ? "-1" : "1"}`
            : undefined,
        q,
        ...filterFields,
      };

      const url = `${apiUrl}/api/v2/${getApiPath(resourcePath)}?${stringify(query)}`;

      try {
        const res = await httpClient(url, { headers });

        // Handle case where API returns an array directly (like custom_domains)
        if (Array.isArray(res.json)) {
          return {
            data: res.json.map((item) => ({
              id: item.id,
              ...item,
            })),
            total: res.json.length,
          };
        }

        // Handle standard case where API returns an object with a property
        // named after the resource. The response key is snake_cased (e.g.
        // `proxy_routes`) while the resource path is hyphenated.
        const list =
          res.json[resource] ?? res.json[resource.replace(/-/g, "_")];
        return {
          data:
            list?.map((item: ApiRecord) => ({
              id: item.id,
              ...item,
            })) || [],
          total: res.json.total || res.json.length || 0,
        };
      } catch (error) {
        console.error("Error in getList:", error);
        throw error;
      }
    },

    getOne: async (resource, params) => {
      const managementClient = await getManagementClient();

      if (resource === "partials") {
        const partials = await managementClient.prompts.partials.get(
          params.id as PartialGroup,
        );
        return { data: { id: params.id, partials } };
      }

      // Virtual scopes-as-one under resource servers
      if (resource === SCOPE_RES) {
        const [rsId, value] = splitScopeId(params.id);
        if (!rsId) throw new Error(`Invalid scope id: ${params.id}`);
        const { scopes } = await fetchResourceServerScopes(
          managementClient,
          rsId,
        );
        const scope = scopes.find(
          (s: Management.ResourceServerScope) => s.value === value,
        );
        if (!scope) {
          throw new Error(
            `Scope "${value}" not found on resource server ${rsId}`,
          );
        }
        return {
          data: {
            id: params.id,
            resource_server_id: rsId,
            value: scope.value,
            description: scope.description,
          },
        };
      }

      // SDK resource handlers for getOne
      const sdkGetHandlers: Record<
        string,
        { fetch: (id: string) => Promise<unknown>; idKey: string }
      > = {
        users: {
          fetch: (id) => managementClient.users.get(id),
          idKey: "user_id",
        },
        clients: {
          fetch: (id) => managementClient.clients.get(id),
          idKey: "client_id",
        },
        "custom-domains": {
          fetch: (id) => managementClient.customDomains.get(id),
          idKey: "custom_domain_id",
        },
        flows: {
          fetch: (id) => managementClient.flows.get(id),
          idKey: "id",
        },
        hooks: {
          fetch: (id) => managementClient.hooks.get(id),
          idKey: "hook_id",
        },
        forms: {
          fetch: (id) => managementClient.forms.get(id),
          idKey: "id",
        },
      };

      const handler = sdkGetHandlers[resource];
      if (handler) {
        const result = await handler.fetch(params.id as string);
        // Unwrap SDK response wrapper if present
        const data = unwrapResponse(result);

        const record = {
          id: data[handler.idKey] || data.id,
          ...data,
        };

        return {
          data:
            resource === "custom-domains"
              ? unflattenDomainMetadata(record)
              : record,
        };
      }

      // Handle singleton resources
      if (resource === "branding") {
        const result = await managementClient.branding.get();
        let themes: Awaited<
          ReturnType<typeof managementClient.branding.themes.getDefault>
        > | null = null;
        try {
          themes = await managementClient.branding.themes.getDefault();
        } catch (err) {
          // 404 means no default theme configured yet; anything else should bubble up.
          if (
            !(
              err &&
              typeof err === "object" &&
              "statusCode" in err &&
              err.statusCode === 404
            )
          ) {
            throw err;
          }
        }
        return {
          data: {
            ...result,
            themes,
            id: resource,
          },
        };
      }

      if (resource === "settings") {
        const result = await managementClient.tenants.settings.get();
        return {
          data: {
            ...result,
            id: resource,
          },
        };
      }

      if (resource === "attack-protection") {
        const [bpd, bf, sip] = await Promise.all([
          managementClient.attackProtection.breachedPasswordDetection.get(),
          managementClient.attackProtection.bruteForceProtection.get(),
          managementClient.attackProtection.suspiciousIpThrottling.get(),
        ]);
        return {
          data: {
            id: resource,
            breached_password_detection: bpd,
            brute_force_protection: bf,
            suspicious_ip_throttling: sip,
          },
        };
      }

      if (resource === "mfa") {
        const result = await managementClient.tenants.settings.get();
        return {
          data: { ...result, id: resource },
        };
      }

      // Handle prompts singleton resource
      if (resource === "prompts") {
        const headers = createHeaders(tenantId);
        try {
          const res = await httpClient(`${apiUrl}/api/v2/prompts`, { headers });
          // Also fetch custom text entries list
          let customTextEntries: Array<{ prompt: string; language: string }> =
            [];
          try {
            const customTextRes = await httpClient(
              `${apiUrl}/api/v2/prompts/custom-text`,
              { headers },
            );
            customTextEntries = customTextRes.json || [];
          } catch {
            // Custom text list might not exist yet
          }
          return {
            data: { ...res.json, customTextEntries, id: resource },
          };
        } catch (error) {
          console.error("Error fetching prompts:", error);
          return {
            data: { id: resource, customTextEntries: [] },
          };
        }
      }

      // Handle email-providers singleton resource
      if (resource === "email-providers") {
        try {
          const provider = await managementClient.emails.provider.get();
          return {
            data: { ...provider, id: resource },
          };
        } catch (err) {
          // No provider configured yet — render the form with sensible defaults.
          // Anything other than 404 (401/403/5xx/network) should bubble up.
          if (
            err &&
            typeof err === "object" &&
            "statusCode" in err &&
            err.statusCode === 404
          ) {
            return {
              data: { id: resource, enabled: true, credentials: {} },
            };
          }
          throw err;
        }
      }

      // Handle email-templates: fetch the tenant override, fall back to a
      // blank record on 404 so the form can render with defaults.
      if (resource === "email-templates") {
        const defaults = await fetchEmailTemplateDefaults(
          apiUrl,
          httpClient,
          tenantId,
        );
        const record = await fetchEmailTemplateRecord(
          managementClient,
          String(params.id),
          defaults,
        );
        return { data: record };
      }

      // Handle custom-text resource (individual entries)
      if (resource === "custom-text") {
        // ID format is "prompt:language"
        const [prompt, language] = String(params.id).split(":") || [];
        if (!prompt || !language) {
          throw new Error("Invalid custom-text ID format");
        }
        try {
          const result = await managementClient.prompts.customText.get(
            prompt as CustomTextPrompt,
            language as CustomTextLanguage,
          );
          return {
            data: {
              id: params.id,
              prompt,
              language,
              texts: result || {},
            },
          };
        } catch (error) {
          console.error("Error fetching custom-text:", error);
          return {
            data: { id: params.id, prompt, language, texts: {} },
          };
        }
      }

      // Handle stats/active-users endpoint
      if (resource === "stats/active-users") {
        try {
          const result = await managementClient.stats.getActiveUsersCount();
          const count = typeof result === "number" ? result : 0;
          return {
            data: {
              id: "count",
              count,
            },
          };
        } catch (error) {
          console.error("Error fetching active users:", error);
          return {
            data: {
              id: "count",
              count: 0,
            },
          };
        }
      }

      // Special handling for tenants - fetch from list and find by ID
      if (resource === "tenants") {
        const headers = createHeaders(tenantId);

        try {
          const res = await httpClient(`${apiUrl}/api/v2/tenants`, {
            headers,
          });

          const tenants = res.json.tenants || [];
          const tenant = tenants.find(
            (t: ApiRecord) => t.id === params.id || t.tenant_id === params.id,
          );

          if (tenant) {
            return {
              data: {
                id: tenant.id || tenant.tenant_id,
                ...tenant,
              },
            };
          }

          return {
            data: {
              id: params.id,
              name: params.id,
            },
          };
        } catch (error) {
          console.warn(`Could not fetch tenant ${params.id}:`, error);
          return {
            data: {
              id: params.id,
              name: params.id,
            },
          };
        }
      }

      // Action executions also need the per-action captured console output,
      // which lives at a sibling path (/logs) rather than on the main record.
      if (resource === "action-executions") {
        const headers = createHeaders(tenantId);
        const base = `${apiUrl}/api/v2/actions/executions/${params.id}`;
        const [execRes, logsRes] = await Promise.all([
          httpClient(base, { headers }),
          httpClient(`${base}/logs`, { headers }).catch(() => ({
            json: { logs: [] },
          })),
        ]);
        return {
          data: {
            ...execRes.json,
            id: execRes.json.id || params.id,
            execution_logs: logsRes.json?.logs ?? [],
          },
        };
      }

      // Logs use log_id as their identifier, not id.
      if (resource === "logs") {
        const headers = createHeaders(tenantId);
        const { json } = await httpClient(
          `${apiUrl}/api/v2/logs/${params.id}`,
          { headers },
        );
        return {
          data: {
            ...json,
            id: json.log_id || json.id || params.id,
          },
        };
      }

      // HTTP for other resources
      const headers = createHeaders(tenantId);
      return httpClient(
        `${apiUrl}/api/v2/${getApiPath(resource)}/${params.id}`,
        {
          headers,
        },
      ).then(({ json }) => ({
        data: {
          id: json.id,
          ...json,
        },
      }));
    },

    getMany: (resourcePath, params) => {
      const query = `id:(${params.ids.join(" ")})`;

      const headers = createHeaders(tenantId);
      const url = `${apiUrl}/api/v2/${getApiPath(resourcePath)}?q=${query}`;
      return httpClient(url, { headers }).then(({ json }) => ({
        data: {
          id: json.id,
          ...json,
        },
      }));
    },

    getManyReference: async (resource, params) => {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const managementClient = await getManagementClient();

      // Build common query params for pagination
      const buildPaginationParams = () => ({
        page: page - 1,
        per_page: perPage,
      });

      // Virtual scopes nested under a resource server
      if (resource === SCOPE_RES && params.target === "resource_server_id") {
        const rsId = String(params.id);
        const { scopes } = await fetchResourceServerScopes(
          managementClient,
          rsId,
        );
        return clientSideListHandler({
          data: scopes.map((s: Management.ResourceServerScope) => ({
            id: `${rsId}:${s.value}`,
            resource_server_id: rsId,
            value: s.value,
            description: s.description,
          })),
          page,
          perPage: perPage || 25,
          sortField: field,
          sortOrder: order,
          searchQuery: params.filter?.q,
          searchFields: ["value", "description"],
        });
      }

      // Sessions nested under users
      if (resource === "sessions") {
        // Sessions are user-specific, use HTTP
        const headers = createHeaders(tenantId);
        const res = await httpClient(
          `${apiUrl}/api/v2/users/${params.id}/sessions?${stringify({
            include_totals: true,
            ...buildPaginationParams(),
            sort: `${field}:${order === "DESC" ? "-1" : "1"}`,
          })}`,
          { headers },
        );
        return {
          data: res.json.sessions.map((item: ApiRecord) => ({
            id: item.id,
            ...item,
          })),
          total: res.json.total ?? res.json.length ?? 0,
        };
      }

      // Refresh tokens nested under users
      if (resource === "refresh-tokens" && params.target === "user_id") {
        const headers = createHeaders(tenantId);
        const res = await httpClient(
          `${apiUrl}/api/v2/users/${encodeURIComponent(String(params.id))}/refresh-tokens?${stringify(
            {
              include_totals: true,
              ...buildPaginationParams(),
              sort: `${field}:${order === "DESC" ? "-1" : "1"}`,
            },
          )}`,
          { headers },
        );
        const tokens: RefreshTokenResponse[] = res.json.tokens || [];
        return {
          data: tokens.map((item) => ({ ...item, id: item.id })),
          total: res.json.total ?? res.json.length ?? tokens.length,
        };
      }

      // OAuth grants (Auth0-style /api/v2/grants?user_id=...)
      if (resource === "grants" && params.target === "user_id") {
        const headers = createHeaders(tenantId);
        const res = await httpClient(
          `${apiUrl}/api/v2/grants?${stringify({
            user_id: params.id,
            include_totals: true,
            ...buildPaginationParams(),
            sort: `${field}:${order === "DESC" ? "-1" : "1"}`,
          })}`,
          { headers },
        );
        const grants = res.json.grants || [];
        return {
          data: grants.map((item: ApiRecord) => ({ id: item.id, ...item })),
          total: res.json.total ?? res.json.length ?? grants.length,
        };
      }

      // Permissions nested under users
      if (resource === "permissions" && params.target === "user_id") {
        const result = await managementClient.users.permissions.list(
          params.id as string,
          { page: 0, per_page: 100 },
        );
        const permissions = unwrapResponse<ListResponse<"permissions">>(result);
        const permissionsArray = Array.isArray(permissions)
          ? permissions
          : permissions.permissions || [];
        return clientSideListHandler({
          data: permissionsArray.map((item: ApiRecord) => ({
            id: `${item.resource_server_identifier}:${item.permission_name}`,
            ...item,
          })),
          page,
          perPage: perPage || 25,
          sortField: field,
          sortOrder: order,
          searchQuery: params.filter?.q,
          searchFields: [
            "permission_name",
            "description",
            "resource_server_identifier",
            "resource_server_name",
          ],
        });
      }

      // Permissions nested under roles
      if (resource === "permissions" && params.target === "role_id") {
        const result = await managementClient.roles.permissions.list(
          params.id as string,
          { page: 0, per_page: 100 },
        );
        const permissions = unwrapResponse<ListResponse<"permissions">>(result);
        const permissionsArray = Array.isArray(permissions)
          ? permissions
          : permissions.permissions || [];
        return clientSideListHandler({
          data: permissionsArray.map((item: ApiRecord) => ({
            id: `${item.resource_server_identifier}:${item.permission_name}`,
            ...item,
          })),
          page,
          perPage: perPage || 25,
          sortField: field,
          sortOrder: order,
          searchQuery: params.filter?.q,
          searchFields: [
            "permission_name",
            "description",
            "resource_server_identifier",
            "resource_server_name",
          ],
        });
      }

      // Roles nested under users
      if (resource === "roles" && params.target === "user_id") {
        const result = await managementClient.users.roles.list(
          params.id as string,
        );
        const roles = unwrapResponse(result);
        const rolesArray = Array.isArray(roles) ? roles : [];
        return {
          data: rolesArray.map((item: ApiRecord) => ({
            id: item.id,
            ...item,
          })),
          total: rolesArray.length,
        };
      }

      // Organization members
      if (
        resource === "organization-members" &&
        params.target === "organization_id"
      ) {
        // Use offset pagination (page/per_page) so the admin keeps numbered
        // pages and a total count. `from`/`take` on this endpoint is now
        // Auth0-style keyset checkpoint pagination — `from` is an opaque
        // cursor, not a numeric offset — and returns no total, which the
        // numbered <ListPagination> cannot consume.
        // The SDK types only describe the checkpoint pagination parameters
        const membersQuery = {
          page: page - 1,
          per_page: perPage,
          include_totals: true,
        };
        const result = await managementClient.organizations.members.list(
          params.id as string,
          membersQuery as MembersListQuery,
        );
        const response = unwrapResponse<ListResponse<"members">>(result);
        const membersData = Array.isArray(response)
          ? response
          : response.members || [];
        const total =
          (Array.isArray(response) ? undefined : response.total) ||
          membersData.length;

        return {
          data: membersData.map((item: ApiRecord) => ({
            id: `${params.id}_${item.user_id}`,
            organization_id: params.id,
            ...item,
          })),
          total,
        };
      }

      // Organization invitations
      if (
        resource === "organization-invitations" &&
        params.target === "organization_id"
      ) {
        const result = await managementClient.organizations.invitations.list(
          params.id as string,
          {
            page: page - 1,
            per_page: perPage,
          },
        );
        const response = unwrapResponse<ListResponse<"invitations">>(result);
        const invitationsData = Array.isArray(response)
          ? response
          : response.invitations || [];
        const total =
          (Array.isArray(response) ? undefined : response.total) ||
          invitationsData.length;

        return {
          data: invitationsData.map((item: ApiRecord) => ({
            id: item.id,
            organization_id: params.id,
            ...item,
          })),
          total,
        };
      }

      // User organizations
      if (resource === "user-organizations" && params.target === "user_id") {
        const result = await managementClient.users.organizations.list(
          params.id as string,
          {
            page: page - 1,
            per_page: perPage,
          },
        );
        const response = unwrapResponse<ListResponse<"organizations">>(result);

        let organizationsData: ApiRecord[];
        let total: number;

        if (Array.isArray(response)) {
          organizationsData = response;
          total = response.length;
        } else if (response.organizations) {
          organizationsData = response.organizations;
          total = response.total || organizationsData.length;
        } else {
          organizationsData = [];
          total = 0;
        }

        return {
          data: organizationsData.map((org: ApiRecord) => ({
            id: org.id || org.organization_id,
            user_id: params.id,
            name: org.name,
            display_name: org.display_name,
            branding: org.branding,
            metadata: org.metadata || {},
            token_quota: org.token_quota,
            created_at: org.created_at,
            updated_at: org.updated_at,
            ...org,
          })),
          total,
        };
      }

      // Client grants filtered by client_id
      if (resource === "client-grants" && params.target === "client_id") {
        const result = await managementClient.clientGrants.list({
          ...buildPaginationParams(),
          client_id: params.id as string,
        });
        const response = unwrapResponse<ListResponse<"client_grants">>(result);
        const grantsData = Array.isArray(response)
          ? response
          : response.client_grants || [];
        const total =
          (Array.isArray(response) ? undefined : response.total) ||
          grantsData.length;

        return {
          data: grantsData.map((item: ApiRecord) => ({
            id: item.id,
            ...item,
          })),
          total,
        };
      }

      // Logs filtered by user_id - includes logs from linked accounts
      if (resource === "logs" && params.target === "user_id") {
        const headers = createHeaders(tenantId);
        const { q: rawQ, from, to, ...filterPairs } = params.filter || {};
        const extraLucene = Object.entries(filterPairs)
          .filter(([, v]) => v !== undefined && v !== null && v !== "")
          .map(([k, v]) => `${k}:"${escapeLuceneValue(v)}"`)
          .join(" ");
        const mergedQ =
          [rawQ, extraLucene].filter(Boolean).join(" ") || undefined;
        const query = {
          page: page - 1,
          per_page: perPage,
          sort:
            field && order
              ? `${field}:${order === "DESC" ? "-1" : "1"}`
              : undefined,
          include_totals: true,
          q: mergedQ,
          from,
          to,
        };
        const url = `${apiUrl}/api/v2/users/${encodeURIComponent(
          String(params.id),
        )}/logs?${stringify(query)}`;

        const res = await httpClient(url, { headers });
        const response = res.json;
        const logsData = response.logs || response || [];
        const logsArray = Array.isArray(logsData) ? logsData : [];

        return {
          data: logsArray.map((log: ApiRecord) => ({
            id: log.log_id || log.id,
            ...log,
          })),
          total: response.total ?? response.length ?? logsArray.length,
        };
      }

      // Default implementation for other resources - use HTTP fallback
      const headers = createHeaders(tenantId);
      const res = await httpClient(
        `${apiUrl}/api/v2/${getApiPath(resource)}?${stringify({
          include_totals: true,
          ...buildPaginationParams(),
          sort: `${field}:${order === "DESC" ? "-1" : "1"}`,
          q: `user_id:"${escapeLuceneValue(params.id)}"`,
        })}`,
        { headers },
      );

      return {
        data: res.json[resource].map((item: ApiRecord) => ({
          id: item.id,
          ...item,
        })),
        total: res.json.total,
      };
    },

    update: async (resource, params) => {
      const cleanParams = removeExtraFields(params);
      const managementClient = await getManagementClient();
      const headers = createHeaders(tenantId);

      if (resource === "partials") {
        await managementClient.prompts.partials.set(
          params.id as PartialGroup,
          params.data.partials,
        );
        return { data: { id: params.id, partials: params.data.partials } };
      }

      // Virtual scope update: read-modify-write the parent's scopes array
      if (resource === SCOPE_RES) {
        const [rsId, originalValue] = splitScopeId(params.id);
        if (!rsId) throw new Error(`Invalid scope id: ${params.id}`);
        const { scopes } = await fetchResourceServerScopes(
          managementClient,
          rsId,
        );
        const idx = scopes.findIndex(
          (s: Management.ResourceServerScope) => s.value === originalValue,
        );
        if (idx === -1) {
          throw new Error(`Scope "${originalValue}" no longer exists`);
        }
        const newValue = cleanParams.data.value || originalValue;
        const newDescription =
          cleanParams.data.description ?? scopes[idx].description ?? "";
        if (
          newValue !== originalValue &&
          scopes.some(
            (s: Management.ResourceServerScope, i: number) =>
              i !== idx && s.value === newValue,
          )
        ) {
          throw new Error(`Scope "${newValue}" already exists`);
        }
        const newScopes = scopes.map(
          (s: Management.ResourceServerScope, i: number) =>
            i === idx ? { value: newValue, description: newDescription } : s,
        );
        await managementClient.resourceServers.update(rsId, {
          scopes: newScopes,
        });
        return {
          data: {
            id: `${rsId}:${newValue}`,
            resource_server_id: rsId,
            value: newValue,
            description: newDescription,
          },
        };
      }

      // Handle singleton resources
      if (resource === "settings") {
        const result = await managementClient.tenants.settings.update(
          cleanParams.data,
        );
        return {
          data: { ...result, id: resource },
        };
      }

      if (resource === "mfa") {
        // The MFA form only edits mfa.* and guardian_mfa_page.*; everything
        // else on `tenants.settings` is left untouched by sending only those
        // keys. Drop synthetic id before submitting.
        const { id: _id, ...rest } = cleanParams.data;
        const patch: Record<string, unknown> = {};
        if (rest.mfa !== undefined) patch.mfa = rest.mfa;
        if (rest.guardian_mfa_page !== undefined) {
          patch.guardian_mfa_page = rest.guardian_mfa_page;
        }
        const result = await managementClient.tenants.settings.update(patch);
        return { data: { ...result, id: resource } };
      }

      if (resource === "attack-protection") {
        const ap = managementClient.attackProtection;
        // Run sequentially so that a failure aborts subsequent updates,
        // and the returned data reflects only operations that succeeded.
        const bpd = await ap.breachedPasswordDetection.update(
          cleanParams.data.breached_password_detection ?? {},
        );
        const bf = await ap.bruteForceProtection.update(
          cleanParams.data.brute_force_protection ?? {},
        );
        const sip = await ap.suspiciousIpThrottling.update(
          cleanParams.data.suspicious_ip_throttling ?? {},
        );
        return {
          data: {
            id: resource,
            breached_password_detection: bpd,
            brute_force_protection: bf,
            suspicious_ip_throttling: sip,
          },
        };
      }

      // Handle prompts singleton resource
      if (resource === "prompts") {
        const headers = createHeaders(tenantId);
        headers.set("Content-Type", "application/json");
        // Don't send customTextEntries to the settings endpoint
        const { customTextEntries, ...promptsData } = cleanParams.data;
        const res = await httpClient(`${apiUrl}/api/v2/prompts`, {
          method: "PATCH",
          headers,
          body: JSON.stringify(promptsData),
        });
        return {
          data: { ...res.json, customTextEntries, id: resource },
        };
      }

      // Handle email-providers singleton resource. Match Auth0 semantics:
      // PATCH to update, POST to create. PATCH 404s when no row exists yet,
      // so we fall back to POST in that case.
      if (resource === "email-providers") {
        const { id: _id, ...body } = cleanParams.data;
        try {
          const updated = await managementClient.emails.provider.update(body);
          return {
            data: { ...updated, id: resource },
          };
        } catch (err: unknown) {
          const status = (err as { statusCode?: number } | undefined)
            ?.statusCode;
          if (status !== 404) throw err;
          const created = await managementClient.emails.provider.create(
            body as Parameters<
              typeof managementClient.emails.provider.create
            >[0],
          );
          return {
            data: { ...created, id: resource },
          };
        }
      }

      // Handle email-templates: PUT upserts by template name, so we always
      // call `set` regardless of override existence.
      if (resource === "email-templates") {
        const templateName = String(params.id);
        const body = cleanParams.data as Parameters<
          typeof managementClient.emailTemplates.set
        >[1];
        const updated = await managementClient.emailTemplates.set(
          templateName as Parameters<
            typeof managementClient.emailTemplates.set
          >[0],
          body,
        );
        return {
          data: {
            ...updated,
            id: templateName,
            template: templateName,
            label: getTemplateLabel(templateName),
            is_override: true,
          },
        };
      }

      // Handle custom-text resource
      if (resource === "custom-text") {
        // ID format is "prompt:language"
        const [prompt, language] = String(params.id).split(":");
        if (!prompt || !language) {
          throw new Error("Invalid custom-text ID format");
        }
        await managementClient.prompts.customText.set(
          prompt as CustomTextPrompt,
          language as CustomTextLanguage,
          cleanParams.data.texts || {},
        );
        return {
          data: {
            id: params.id,
            prompt,
            language,
            texts: cleanParams.data.texts || {},
          },
        };
      }

      // Special handling for branding to update theme data separately
      if (resource === "branding") {
        // Extract themes from the payload - it's updated via a separate endpoint
        const { themes, ...brandingData } = cleanParams.data;

        // Update branding (without themes)
        const brandingResult =
          await managementClient.branding.update(brandingData);

        // Update themes if provided
        const result: ApiRecord = {
          id: resource,
          ...brandingResult,
        };

        if (themes) {
          // Use HTTP directly since the SDK doesn't have this method
          const themeResponse = await httpClient(
            `${apiUrl}/api/v2/branding/themes/default`,
            {
              headers,
              method: "PATCH",
              body: JSON.stringify(themes),
            },
          );
          result.themes = themeResponse.json;
        }

        return { data: result };
      }

      // SDK-handled resources
      if (resource === "users") {
        const result = await managementClient.users.update(
          params.id as string,
          cleanParams.data,
        );
        return {
          data: {
            id: result.user_id,
            ...result,
          },
        };
      }

      if (resource === "clients") {
        const result = await managementClient.clients.update(
          params.id as string,
          cleanParams.data,
        );
        return {
          data: {
            id: result.client_id,
            ...result,
          },
        };
      }

      if (resource === "connections") {
        const result = await managementClient.connections.update(
          params.id as string,
          cleanParams.data,
        );
        return {
          data: {
            id: result.id,
            ...result,
          },
        };
      }

      if (resource === "roles") {
        const result = await managementClient.roles.update(
          params.id as string,
          cleanParams.data,
        );
        return {
          data: {
            id: result.id,
            ...result,
          },
        };
      }

      if (resource === "resource-servers") {
        const result = await managementClient.resourceServers.update(
          params.id as string,
          cleanParams.data,
        );
        return {
          data: {
            id: result.id,
            ...result,
          },
        };
      }

      if (resource === "organizations") {
        const result = await managementClient.organizations.update(
          params.id as string,
          cleanParams.data,
        );
        return {
          data: {
            id: result.id,
            ...result,
          },
        };
      }

      if (resource === "rules") {
        const result = await managementClient.rules.update(
          params.id as string,
          cleanParams.data,
        );
        return {
          data: {
            id: result.id,
            ...result,
          },
        };
      }

      if (resource === "client-grants") {
        const result = await managementClient.clientGrants.update(
          params.id as string,
          cleanParams.data,
        );
        return {
          data: {
            id: result.id,
            ...result,
          },
        };
      }

      if (resource === "custom-domains") {
        // Auth0 PATCH /custom-domains/{id} only accepts tls_policy and
        // custom_client_ip_header; domain_metadata is authhero's extension.
        // Everything else (custom_domain_id, domain, primary, status, type,
        // origin_domain_name, verification) is read-only and the server
        // rejects the body if those are present alongside null sentinels.
        const data = cleanParams.data as Record<string, unknown>;
        const patch: Record<string, unknown> = {};
        if (typeof data.tls_policy === "string" && data.tls_policy !== "") {
          patch.tls_policy = data.tls_policy;
        }
        if (
          typeof data.custom_client_ip_header === "string" &&
          data.custom_client_ip_header !== ""
        ) {
          patch.custom_client_ip_header = data.custom_client_ip_header;
        }
        if (
          data.domain_metadata !== undefined &&
          data.domain_metadata !== null
        ) {
          patch.domain_metadata = data.domain_metadata;
        }

        const result = await managementClient.customDomains.update(
          params.id as string,
          patch,
        );
        return {
          data: {
            id: result.custom_domain_id || (result as { id?: string }).id,
            ...result,
          },
        };
      }

      if (resource === TOKEN_EXCHANGE_PROFILES_RES) {
        const res = await httpClient(
          `${apiUrl}/api/v2/${TOKEN_EXCHANGE_PROFILES_RES}/${encodeURIComponent(String(params.id))}`,
          {
            headers,
            method: "PATCH",
            body: JSON.stringify(
              pickTokenExchangeProfileUpdate(cleanParams.data),
            ),
          },
        );
        return { data: { ...res.json, id: res.json.id ?? params.id } };
      }

      // HTTP fallback for other resources
      return httpClient(
        `${apiUrl}/api/v2/${getApiPath(resource)}/${params.id}`,
        {
          headers,
          method: "PATCH",
          body: JSON.stringify(cleanParams.data),
        },
      ).then(({ json }) => {
        if (!json.id) {
          // Try singular form of resource name (e.g., hooks -> hook_id)
          const singularResource = resource.endsWith("s")
            ? resource.slice(0, -1)
            : resource;
          json.id =
            json[`${singularResource}_id`] || json[`${resource}_id`] || json.id;
        }
        return { data: json };
      });
    },

    updateMany: () => Promise.reject("not supporting updateMany"),

    create: async (resource, params) => {
      const headers = new Headers({ "content-type": "application/json" });
      if (tenantId) headers.set("tenant-id", tenantId);
      const managementClient = await getManagementClient();

      // Virtual scope create: append to the parent's scopes array
      if (resource === SCOPE_RES) {
        const { resource_server_id: rsId, value, description } = params.data;
        if (!rsId || !value) {
          throw new Error("resource_server_id and value are required");
        }
        const { scopes } = await fetchResourceServerScopes(
          managementClient,
          rsId,
        );
        if (
          scopes.some((s: Management.ResourceServerScope) => s.value === value)
        ) {
          throw new Error(`Scope "${value}" already exists`);
        }
        const newScope = { value, description: description || "" };
        await managementClient.resourceServers.update(rsId, {
          scopes: [...scopes, newScope],
        });
        return {
          data: {
            id: `${rsId}:${value}`,
            resource_server_id: rsId,
            value,
            description: newScope.description,
          },
        };
      }

      // Handle custom-text resource
      if (resource === "custom-text") {
        const { prompt, language, texts } = params.data;
        if (!prompt || !language) {
          throw new Error("prompt and language are required");
        }
        await managementClient.prompts.customText.set(
          prompt as CustomTextPrompt,
          language as CustomTextLanguage,
          texts || {},
        );
        return {
          data: {
            id: `${prompt}:${language}`,
            prompt,
            language,
            texts: texts || {},
          },
        };
      }

      // Helper for POST requests
      const post = async (endpoint: string, body: unknown) =>
        httpClient(`${apiUrl}/api/v2/${endpoint}`, {
          method: "POST",
          body: JSON.stringify(body),
          headers,
        });

      // Adapt a typed SDK create method to take the untyped form data
      const sdkCreate =
        <A>(fn: (data: A) => Promise<unknown>) =>
        (data: ApiRecord) =>
          fn(data as A) as Promise<ApiRecord>;

      // SDK resource handlers for create
      const sdkCreateHandlers: Record<
        string,
        { create: (data: ApiRecord) => Promise<ApiRecord>; idKey: string }
      > = {
        users: {
          create: sdkCreate(
            (data: Parameters<typeof managementClient.users.create>[0]) =>
              managementClient.users.create(data),
          ),
          idKey: "user_id",
        },
        clients: {
          create: sdkCreate(
            (data: Parameters<typeof managementClient.clients.create>[0]) =>
              managementClient.clients.create(data),
          ),
          idKey: "client_id",
        },
        connections: {
          create: sdkCreate(
            (data: Parameters<typeof managementClient.connections.create>[0]) =>
              managementClient.connections.create(data),
          ),
          idKey: "id",
        },
        roles: {
          create: sdkCreate(
            (data: Parameters<typeof managementClient.roles.create>[0]) =>
              managementClient.roles.create(data),
          ),
          idKey: "id",
        },
        "resource-servers": {
          create: sdkCreate(
            (
              data: Parameters<
                typeof managementClient.resourceServers.create
              >[0],
            ) => managementClient.resourceServers.create(data),
          ),
          idKey: "id",
        },
        organizations: {
          create: sdkCreate(
            (
              data: Parameters<typeof managementClient.organizations.create>[0],
            ) => managementClient.organizations.create(data),
          ),
          idKey: "id",
        },
        rules: {
          create: sdkCreate(
            (data: Parameters<typeof managementClient.rules.create>[0]) =>
              managementClient.rules.create(data),
          ),
          idKey: "id",
        },
        "client-grants": {
          create: sdkCreate(
            (
              data: Parameters<typeof managementClient.clientGrants.create>[0],
            ) => managementClient.clientGrants.create(data),
          ),
          idKey: "id",
        },
        "custom-domains": {
          create: sdkCreate(
            (
              data: Parameters<typeof managementClient.customDomains.create>[0],
            ) => managementClient.customDomains.create(data),
          ),
          idKey: "custom_domain_id",
        },
      };

      const handler = sdkCreateHandlers[resource];
      if (handler) {
        const result = await handler.create(params.data);
        return {
          data: {
            id: result[handler.idKey] || result.id,
            ...result,
          },
        };
      }

      // Organization invitations
      if (resource === "organization-invitations") {
        const { organization_id, ...inviteData } = params.data;
        const res = await post(
          `organizations/${organization_id}/invitations`,
          inviteData,
        );
        return {
          data: {
            id: res.json.id,
            organization_id,
            ...res.json,
          },
        };
      }

      // Organization members
      if (resource === "organization-members") {
        const { organization_id, user_id, user_ids } = params.data;
        const usersToAdd = user_ids || [user_id];
        const res = await post(`organizations/${organization_id}/members`, {
          members: usersToAdd,
        });
        return {
          data: {
            id: `${organization_id}_${usersToAdd.join("_")}`,
            organization_id,
            user_id: user_id || usersToAdd[0],
            members: usersToAdd,
            ...res.json,
          },
        };
      }

      // User organizations (same endpoint as org members)
      if (resource === "user-organizations") {
        const { organization_id, user_id, user_ids } = params.data;
        const usersToAdd = user_ids || [user_id];
        const res = await post(`organizations/${organization_id}/members`, {
          members: usersToAdd,
        });
        return {
          data: {
            id: organization_id,
            user_id: user_id || usersToAdd[0],
            members: usersToAdd,
            organization_id,
            ...res.json,
          },
        };
      }

      // User identity link (Auth0 SDK)
      const userIdentitiesMatch = resource.match(
        /^users\/([^/]+)\/identities$/,
      );
      if (userIdentitiesMatch?.[1]) {
        const primaryUserId = userIdentitiesMatch[1];
        // Auth0 expects either link_with=<JWT> or { provider, user_id }.
        // The admin UI passes link_with=<raw user_id> (e.g. "auth0|abc"),
        // so split on "|" into provider + user_id when it's not a JWT.
        let payload = params.data;
        const linkWith = params.data?.link_with;
        if (typeof linkWith === "string" && linkWith.includes("|")) {
          const sep = linkWith.indexOf("|");
          if (sep > 0) {
            const { link_with, ...rest } = params.data;
            payload = {
              ...rest,
              provider: linkWith.slice(0, sep),
              user_id: linkWith.slice(sep + 1),
            };
          }
        }
        const result = await managementClient.users.identities.link(
          primaryUserId,
          payload,
        );
        const identities =
          (result as { data?: unknown }).data || unwrapResponse(result);
        return {
          data: {
            id: primaryUserId,
            identities,
          },
        };
      }

      // User roles assignment
      const userRolesMatch = resource.match(/^users\/([^/]+)\/roles$/);
      if (userRolesMatch) {
        const res = await post(resource, params.data);
        const userId = userRolesMatch[1];
        const roleIds = params.data?.roles || [];
        return {
          data: {
            id: `${userId}_${roleIds.join("_")}`,
            user_id: userId,
            roles: roleIds,
            ...res.json,
          },
        };
      }

      if (resource === TOKEN_EXCHANGE_PROFILES_RES) {
        const res = await post(
          TOKEN_EXCHANGE_PROFILES_RES,
          pickTokenExchangeProfileCreate(params.data),
        );
        return { data: { ...res.json, id: res.json.id } };
      }

      // Default create (for endpoints not in SDK)
      // Clean up null values from form data
      const cleanedData = removeNullValues(params.data);
      const res = await post(getApiPath(resource), cleanedData);
      // Try singular form of resource name (e.g., hooks -> hook_id)
      const singularResource = resource.endsWith("s")
        ? resource.slice(0, -1)
        : resource;
      return {
        data: {
          ...res.json,
          id:
            res.json[`${singularResource}_id`] ||
            res.json[`${resource}_id`] ||
            res.json.id,
        },
      };
    },

    delete: async (resource, params) => {
      const managementClient = await getManagementClient();
      const headers = new Headers({ "content-type": "application/json" });
      if (tenantId) headers.set("tenant-id", tenantId);

      // Virtual scope delete: filter out from the parent's scopes array
      if (resource === SCOPE_RES) {
        const [rsId, value] = splitScopeId(params.id);
        if (!rsId) throw new Error(`Invalid scope id: ${params.id}`);
        const { scopes } = await fetchResourceServerScopes(
          managementClient,
          rsId,
        );
        const newScopes = scopes.filter(
          (s: Management.ResourceServerScope) => s.value !== value,
        );
        await managementClient.resourceServers.update(rsId, {
          scopes: newScopes,
        });
        return { data: { id: params.id } };
      }

      // Handle custom-text resource
      if (resource === "custom-text") {
        // ID format is "prompt:language"
        const [prompt, language] = String(params.id).split(":");
        if (!prompt || !language) {
          throw new Error("Invalid custom-text ID format");
        }
        // Auth0 SDK doesn't have delete, so we set to empty object
        // Our backend also supports DELETE, but using set({}) is compatible with Auth0
        await managementClient.prompts.customText.set(
          prompt as CustomTextPrompt,
          language as CustomTextLanguage,
          {},
        );
        return { data: { id: params.id } };
      }

      // Helper for DELETE requests
      const del = async (endpoint: string, body?: unknown) =>
        httpClient(`${apiUrl}/api/v2/${endpoint}`, {
          method: "DELETE",
          headers,
          body: body ? JSON.stringify(body) : undefined,
        });

      // Email templates: DELETE the tenant override (reverts to bundled
      // default). authhero extension; not in Auth0's contract.
      if (resource === "email-templates") {
        try {
          await del(`email-templates/${encodeURIComponent(String(params.id))}`);
        } catch (err: unknown) {
          const e = err as { status?: number; statusCode?: number } | undefined;
          const status = e?.status ?? e?.statusCode;
          // 404 means no override existed — already at default, treat as success.
          if (status !== 404) throw err;
        }
        return { data: { id: params.id } };
      }

      // Handle email-providers singleton resource — DELETE returns 204.
      if (resource === "email-providers") {
        try {
          await managementClient.emails.provider.delete();
        } catch (err: unknown) {
          // 404 means already deleted — treat as success.
          const status = (err as { statusCode?: number } | undefined)
            ?.statusCode;
          if (status !== 404) throw err;
        }
        return { data: { id: resource } };
      }

      // Organization invitations
      if (resource === "organization-invitations") {
        const invitation_id = params.id;
        const organization_id = params.previousData?.organization_id;

        if (!organization_id || !invitation_id) {
          throw new Error(
            "Missing organization_id or invitation_id for invitation deletion",
          );
        }

        const res = await del(
          `organizations/${organization_id}/invitations/${invitation_id}`,
        );
        return { data: res.json || { id: invitation_id } };
      }

      // Organization members
      if (resource === "organization-members") {
        const { organization_id, user_ids } =
          resolveOrganizationMemberDeletion(params);

        const res = await del(`organizations/${organization_id}/members`, {
          members: user_ids,
        });
        return { data: res.json };
      }

      // User organizations
      if (resource === "user-organizations") {
        let organization_id: string | undefined;
        let user_id: string | undefined;

        if (params.previousData) {
          user_id = params.previousData.user_id;
          organization_id = String(params.id);
        } else if (typeof params.id === "string" && params.id.includes("_")) {
          [user_id, organization_id] = params.id.split("_");
        }

        if (!organization_id || !user_id) {
          throw new Error(
            "Missing organization_id or user_id for user organization deletion",
          );
        }

        await managementClient.organizations.members.delete(organization_id, {
          members: [user_id],
        });
        return { data: { id: params.id } };
      }

      // Custom-domains using SDK
      if (resource === "custom-domains") {
        await managementClient.customDomains.delete(params.id as string);
        return { data: { id: params.id } };
      }

      // 204 with no body: hand react-admin the deleted record back.
      if (resource === TOKEN_EXCHANGE_PROFILES_RES) {
        await httpClient(
          `${apiUrl}/api/v2/${TOKEN_EXCHANGE_PROFILES_RES}/${encodeURIComponent(String(params.id))}`,
          { method: "DELETE", headers },
        );
        return { data: params.previousData ?? { id: params.id } };
      }

      // User identity unlink (Auth0 SDK)
      const userIdentityMatch = resource.match(
        /^users\/([^/]+)\/identities\/([^/]+)$/,
      );
      if (userIdentityMatch?.[1] && userIdentityMatch?.[2]) {
        const [, primaryUserId, provider] = userIdentityMatch;
        type UnlinkProvider = Parameters<
          typeof managementClient.users.identities.delete
        >[1];
        const result = await managementClient.users.identities.delete(
          primaryUserId,
          provider as UnlinkProvider,
          String(params.id),
        );
        const response =
          (result as { data?: unknown; response?: unknown }).data ??
          (result as { response?: unknown }).response ??
          result;
        return { data: { id: params.id, identities: response } };
      }

      // Nested permissions/roles detection
      const isNestedPermissionsDelete =
        /(^|\/)users\/[^/]+\/permissions$/.test(resource) ||
        /(^|\/)roles\/[^/]+\/permissions$/.test(resource);
      const isNestedRolesDelete = /(^|\/)users\/[^/]+\/roles$/.test(resource);
      const isOrgMemberRolesDelete =
        /(^|\/)organizations\/[^/]+\/members\/[^/]+\/roles$/.test(resource);

      const hasId =
        params?.id !== undefined &&
        params?.id !== null &&
        String(params.id) !== "";

      const shouldAppendId =
        hasId &&
        !(
          isNestedPermissionsDelete ||
          isNestedRolesDelete ||
          isOrgMemberRolesDelete
        );

      const resourceUrl = shouldAppendId
        ? `${getApiPath(resource)}/${encodeURIComponent(String(params.id))}`
        : getApiPath(resource);

      let body: unknown = undefined;

      if (isNestedPermissionsDelete) {
        const prev: ApiRecord = params?.previousData ?? {};
        const parsedFromId = (() => {
          if (!hasId)
            return {
              resource_server_identifier: undefined,
              permission_name: undefined,
            };
          try {
            const decoded = decodeURIComponent(String(params.id));
            const [rsi, pname] = decoded.split(":");
            return { resource_server_identifier: rsi, permission_name: pname };
          } catch {
            return {
              resource_server_identifier: undefined,
              permission_name: undefined,
            };
          }
        })();

        body = {
          permissions: [
            {
              permission_name:
                prev.permission_name ?? parsedFromId.permission_name,
              resource_server_identifier:
                prev.resource_server_identifier ??
                parsedFromId.resource_server_identifier,
              ...(prev.organization_id
                ? { organization_id: prev.organization_id }
                : {}),
            },
          ],
        };
      } else if (isNestedRolesDelete || isOrgMemberRolesDelete) {
        const roles = Array.isArray(params?.previousData?.roles)
          ? params.previousData.roles
          : hasId
            ? [String(params.id)]
            : [];
        body = { roles };
      }

      // Update headers for nested endpoints
      if (
        !isNestedPermissionsDelete &&
        !isNestedRolesDelete &&
        !isOrgMemberRolesDelete
      ) {
        headers.set("Content-Type", "text/plain");
      }

      const res = await httpClient(`${apiUrl}/api/v2/${resourceUrl}`, {
        method: "DELETE",
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      return { data: res.json };
    },

    deleteMany: async (resource, params) => {
      const headers = new Headers({ "content-type": "text/plain" });
      if (tenantId) headers.set("tenant-id", tenantId);

      const deletedIds: typeof params.ids = [];

      for (const id of params.ids) {
        const resourceUrl = `${getApiPath(resource)}/${encodeURIComponent(String(id))}`;
        await httpClient(`${apiUrl}/api/v2/${resourceUrl}`, {
          method: "DELETE",
          headers,
        });
        deletedIds.push(id);
      }

      return { data: deletedIds };
    },

    rotateSigningKeys: async (options) => {
      const query = new URLSearchParams({
        type: options?.type ?? "jwt_signing",
      });
      if (options?.validityDays !== undefined) {
        query.set("validity_days", String(options.validityDays));
      }
      if (options?.activateInDays !== undefined) {
        query.set("activate_in_days", String(options.activateInDays));
      }
      if (options?.graceDays !== undefined) {
        query.set("grace_days", String(options.graceDays));
      }
      await httpClient(`${apiUrl}/api/v2/keys/signing/rotate?${query}`, {
        method: "POST",
        headers: createHeaders(tenantId),
      });
    },

    renewSigningKey: async (kid, options) => {
      const query = new URLSearchParams({
        type: options?.type ?? "jwt_signing",
      });
      if (options?.validityDays !== undefined) {
        query.set("validity_days", String(options.validityDays));
      }
      await httpClient(
        `${apiUrl}/api/v2/keys/signing/${encodeURIComponent(kid)}/renew?${query}`,
        {
          method: "POST",
          headers: createHeaders(tenantId),
        },
      );
    },

    listTenantOperations: async (targetTenantId, params) => {
      const query = new URLSearchParams({
        page: String((params?.page ?? 1) - 1),
        per_page: String(params?.perPage ?? 25),
      });
      const res = await httpClient(
        `${apiUrl}/api/v2/tenants/${encodeURIComponent(targetTenantId)}/operations?${query}`,
        { headers: createHeaders(tenantId) },
      );
      return res.json;
    },

    getTenantOperation: async (operationId) => {
      const res = await httpClient(
        `${apiUrl}/api/v2/operations/${encodeURIComponent(operationId)}`,
        { headers: createHeaders(tenantId) },
      );
      return res.json;
    },

    createTenantOperation: async (targetTenantId, kind, input) => {
      const res = await httpClient(
        `${apiUrl}/api/v2/tenants/${encodeURIComponent(targetTenantId)}/operations`,
        {
          method: "POST",
          headers: createHeaders(tenantId),
          body: JSON.stringify(input ? { kind, input } : { kind }),
        },
      );
      return res.json;
    },

    verifyConfigSync: async (targetTenantId) => {
      const res = await httpClient(
        `${apiUrl}/api/v2/tenants/${encodeURIComponent(targetTenantId)}/config-sync/verify`,
        { headers: createHeaders(tenantId) },
      );
      return res.json;
    },

    getConfigSyncStatus: async (targetTenantId) => {
      const res = await httpClient(
        `${apiUrl}/api/v2/tenants/${encodeURIComponent(targetTenantId)}/config-sync`,
        { headers: createHeaders(tenantId) },
      );
      return res.json;
    },

    listRollouts: async (params) => {
      const query = new URLSearchParams({
        page: String((params?.page ?? 1) - 1),
        per_page: String(params?.perPage ?? 25),
        kind: "config-sync",
      });
      if (params?.status) query.set("status", params.status);
      const res = await httpClient(`${apiUrl}/api/v2/rollouts?${query}`, {
        headers: createHeaders(tenantId),
      });
      return res.json;
    },

    getRollout: async (rolloutId) => {
      const res = await httpClient(
        `${apiUrl}/api/v2/rollouts/${encodeURIComponent(rolloutId)}`,
        { headers: createHeaders(tenantId) },
      );
      return res.json;
    },

    createRollout: async (input) => {
      const res = await httpClient(`${apiUrl}/api/v2/rollouts`, {
        method: "POST",
        headers: createHeaders(tenantId),
        body: JSON.stringify(input),
      });
      return res.json;
    },

    pauseRollout: async (rolloutId, reason) => {
      const res = await httpClient(
        `${apiUrl}/api/v2/rollouts/${encodeURIComponent(rolloutId)}/pause`,
        {
          method: "POST",
          headers: createHeaders(tenantId),
          body: JSON.stringify(reason ? { reason } : {}),
        },
      );
      return res.json;
    },

    resumeRollout: async (rolloutId) => {
      const res = await httpClient(
        `${apiUrl}/api/v2/rollouts/${encodeURIComponent(rolloutId)}/resume`,
        { method: "POST", headers: createHeaders(tenantId), body: "{}" },
      );
      return res.json;
    },

    // --- Tenant-team self-management (#1137) ---
    listTeamMembers: async (params) => {
      const query = stringify({
        page: params?.page,
        per_page: params?.perPage,
        q: params?.q || undefined,
      });
      const res = await httpClient(
        `${apiUrl}/api/v2/tenant-members${query ? `?${query}` : ""}`,
        { headers: createHeaders(tenantId) },
      );
      return res.json;
    },
    addTeamMembers: async (userIds) => {
      const headers = createHeaders(tenantId);
      headers.set("content-type", "application/json");
      return teamChangeResult(
        await httpClient(`${apiUrl}/api/v2/tenant-members`, {
          method: "POST",
          headers,
          body: JSON.stringify({ user_ids: userIds }),
        }),
      );
    },
    removeTeamMembers: async (userIds) => {
      const headers = createHeaders(tenantId);
      headers.set("content-type", "application/json");
      return teamChangeResult(
        await httpClient(`${apiUrl}/api/v2/tenant-members`, {
          method: "DELETE",
          headers,
          body: JSON.stringify({ user_ids: userIds }),
        }),
      );
    },
    assignTeamMemberRoles: async (userId, roleIds) => {
      const headers = createHeaders(tenantId);
      headers.set("content-type", "application/json");
      return teamChangeResult(
        await httpClient(
          `${apiUrl}/api/v2/tenant-members/${encodeURIComponent(userId)}/roles`,
          { method: "POST", headers, body: JSON.stringify({ roles: roleIds }) },
        ),
      );
    },
    removeTeamMemberRoles: async (userId, roleIds) => {
      const headers = createHeaders(tenantId);
      headers.set("content-type", "application/json");
      return teamChangeResult(
        await httpClient(
          `${apiUrl}/api/v2/tenant-members/${encodeURIComponent(userId)}/roles`,
          {
            method: "DELETE",
            headers,
            body: JSON.stringify({ roles: roleIds }),
          },
        ),
      );
    },
    listAssignableTeamRoles: async (q) => {
      const query = stringify({ q: q || undefined, per_page: 100 });
      const res = await httpClient(
        `${apiUrl}/api/v2/tenant-members/roles${query ? `?${query}` : ""}`,
        { headers: createHeaders(tenantId) },
      );
      return res.json;
    },
    listTeamInvitations: async () => {
      const res = await httpClient(
        `${apiUrl}/api/v2/tenant-members/invitations`,
        { headers: createHeaders(tenantId) },
      );
      return res.json;
    },
    createTeamInvitation: async (input) => {
      const headers = createHeaders(tenantId);
      headers.set("content-type", "application/json");
      const res = await httpClient(
        `${apiUrl}/api/v2/tenant-members/invitations`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            invitee: { email: input.email.trim() },
            inviter: input.inviterName ? { name: input.inviterName } : {},
            roles: input.roles,
            send_invitation_email: input.sendInvitationEmail,
          }),
        },
      );
      const change = teamChangeResult(res);
      return change.requested ? change : { ...change, invitation: res.json };
    },
    revokeTeamInvitation: async (invitationId) =>
      teamChangeResult(
        await httpClient(
          `${apiUrl}/api/v2/tenant-members/invitations/${encodeURIComponent(invitationId)}`,
          { method: "DELETE", headers: createHeaders(tenantId) },
        ),
      ),
    listTeamRequests: async (params) => {
      const query = stringify({
        status: params?.status,
        per_page: params?.perPage,
      });
      const res = await httpClient(
        `${apiUrl}/api/v2/tenant-members/requests${query ? `?${query}` : ""}`,
        { headers: createHeaders(tenantId) },
      );
      return res.json.requests;
    },

    revokeUserRefreshTokens: async (userId: string) => {
      await httpClient(
        `${apiUrl}/api/v2/users/${encodeURIComponent(userId)}/refresh-tokens`,
        {
          method: "DELETE",
          headers: createHeaders(tenantId),
        },
      );
    },

    revokeSigningKey: async (kid: string, type?: SigningKeyType) => {
      const query = new URLSearchParams({ type: type ?? "jwt_signing" });
      await httpClient(
        `${apiUrl}/api/v2/keys/signing/${encodeURIComponent(kid)}/revoke?${query}`,
        {
          method: "PUT",
          headers: createHeaders(tenantId),
        },
      );
    },

    uploadCustomDomainCertificate: async (id, cert) => {
      await httpClient(
        `${apiUrl}/api/v2/custom-domains/${encodeURIComponent(id)}/certificate`,
        {
          method: "PUT",
          headers: createHeaders(tenantId),
          body: JSON.stringify(cert),
        },
      );
    },

    sendTestEmailTemplate: async (templateName, payload) => {
      await httpClient(
        `${apiUrl}/api/v2/email-templates/${encodeURIComponent(templateName)}/try`,
        {
          method: "POST",
          headers: createHeaders(tenantId),
          body: JSON.stringify(payload),
        },
      );
    },

    tryHook: async (hookId, payload) => {
      const { json } = await httpClient(
        `${apiUrl}/api/v2/hooks/${encodeURIComponent(hookId)}/try`,
        {
          method: "POST",
          headers: createHeaders(tenantId),
          body: JSON.stringify(payload),
        },
      );
      return json;
    },
    exportTenantData: async (includePasswordHashes) => {
      // Request the gzipped stream (server default) and read it as a Blob via a
      // raw authorized fetch — the text-based httpClient would both corrupt the
      // binary payload and force the whole export into a JS string.
      const params = new URLSearchParams();
      if (includePasswordHashes) {
        params.set("include_password_hashes", "true");
      }
      const query = params.toString();
      const token = await resolveAccessToken(apiUrl, tenantId, domain);
      const headers = createHeaders(tenantId);
      headers.set("Authorization", `Bearer ${token}`);
      const response = await fetch(
        `${apiUrl}/api/v2/tenant-data/export${query ? `?${query}` : ""}`,
        { headers },
      );
      if (!response.ok) {
        throw new Error(
          `Tenant export failed (${response.status} ${response.statusText})`,
        );
      }
      return response.blob();
    },
    importTenantData: async (file, includePasswordHashes) => {
      // Stream the original file straight to the server (it sniffs gzip vs
      // plain by magic bytes), so the UI never decodes the payload in memory.
      const params = new URLSearchParams();
      if (includePasswordHashes) {
        params.set("include_password_hashes", "true");
      }
      const query = params.toString();
      const token = await resolveAccessToken(apiUrl, tenantId, domain);
      const headers = createHeaders(tenantId);
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("content-type", "application/octet-stream");
      const response = await fetch(
        `${apiUrl}/api/v2/tenant-data/import${query ? `?${query}` : ""}`,
        {
          method: "POST",
          headers,
          body: file,
        },
      );
      if (!response.ok) {
        throw new Error(
          `Tenant import failed (${response.status} ${response.statusText})`,
        );
      }
      return response.json();
    },
  };
};
